import { useState } from 'react';
import BackToMap from '../components/BackToMap';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { speciesEmoji } from '../utils/guardian';
import { AXES, STORAGE_KEY, TYPES, questionsFor, scoreAnswers } from '../utils/personality';

/**
 * 观心亭(/guanxin):孩子自己点二选一,照一照自己现在是什么性格
 *
 * - 没有好坏、没有对错,结果只说「现在更偏哪边」,百分比条展示,不贴死标签;
 * - 长处之外只说「可以多练的地方」,不说短处;
 * - 结果只存本机(localStorage),不发到服务器,也不埋点记录结果。
 */

function AxisBar({ axis }) {
  const { left, right, leftPercent, winner } = axis;
  return (
    <div data-testid={`axis-${axis.id}`} className="flex flex-col gap-1">
      <div className="flex justify-between text-sm">
        <span className={winner === left.code ? 'font-extrabold' : 'text-gray-500'}>
          {left.name} {leftPercent}%
        </span>
        <span className={winner === right.code ? 'font-extrabold' : 'text-gray-500'}>
          {100 - leftPercent}% {right.name}
        </span>
      </div>
      <div className="flex h-3 overflow-hidden rounded-full border-2 border-black bg-white">
        <span className="bg-qiji-gold" style={{ width: `${leftPercent}%` }} />
        <span className="bg-qiji-mint" style={{ width: `${100 - leftPercent}%` }} />
      </div>
    </div>
  );
}

function GuanxinPage() {
  const [questState] = useLocalStorage('huaxia_quest_state', null);
  const [saved, setSaved] = useLocalStorage(STORAGE_KEY, {});
  const owner = questState?.sessionId || 'guest';
  const questions = questionsFor(questState?.ageBand);
  const guardianIcon = speciesEmoji(questState?.guardian?.species);
  const guardianName = questState?.guardian?.name || '守护兽';

  const existing = saved?.[owner];
  const [phase, setPhase] = useState(existing ? 'result' : 'intro');
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(existing || null);

  const start = () => {
    setAnswers([]);
    setPhase('ask');
  };

  const choose = (pole) => {
    const next = [...answers, pole];
    if (next.length < questions.length) {
      setAnswers(next);
      return;
    }
    const r = { ...scoreAnswers(next), at: new Date().toISOString() };
    setSaved((prev) => ({ ...(prev || {}), [owner]: r }));
    setResult(r);
    setPhase('result');
  };

  const shell = (children) => (
    <div className="min-h-screen bg-qiji-paper px-4 py-4 text-black">
      <div className="mx-auto flex max-w-md flex-col gap-4">
        <BackToMap />
        {children}
      </div>
    </div>
  );

  if (phase === 'intro') {
    return shell(
      <section className="flex flex-col items-center gap-4 rounded-2xl border-2 border-black bg-white p-5 text-center shadow-brutal-sm">
        <span className="text-5xl">🪞</span>
        <h1 className="text-2xl font-extrabold tracking-widest">观心亭</h1>
        <p className="flex items-start gap-2 text-left">
          <span className="text-3xl">{guardianIcon}</span>
          <span>
            {guardianName}:亭子里有一面观心镜。我问你 {questions.length} 个小问题,每个都选
            <b>更像你</b>的那一个。没有好坏,也没有对错,想到什么就选什么!
          </span>
        </p>
        <button
          type="button"
          data-testid="guanxin-start"
          onClick={start}
          className="w-full rounded-2xl border-2 border-black bg-qiji-gold px-4 py-3 text-lg font-extrabold shadow-brutal-sm active:translate-y-0.5"
        >
          照一照观心镜
        </button>
      </section>,
    );
  }

  if (phase === 'ask') {
    const i = answers.length;
    const cur = questions[i];
    return shell(
      <section className="flex flex-col gap-4">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span data-testid="guanxin-progress">
            第 {i + 1} / {questions.length} 问
          </span>
          {i > 0 && (
            <button
              type="button"
              data-testid="guanxin-back"
              onClick={() => setAnswers(answers.slice(0, -1))}
              className="min-h-[44px] px-2 underline"
            >
              ← 上一问
            </button>
          )}
        </div>
        <div className="h-2 overflow-hidden rounded-full border border-black bg-white">
          <span className="block h-full bg-qiji-gold" style={{ width: `${(i / questions.length) * 100}%` }} />
        </div>
        <p data-testid="guanxin-question" className="py-4 text-center text-xl font-extrabold">
          {cur.text}
        </p>
        {cur.options.map(([text, pole]) => (
          <button
            key={text}
            type="button"
            data-testid="guanxin-option"
            onClick={() => choose(pole)}
            className="min-h-[64px] rounded-2xl border-2 border-black bg-amber-50 px-4 py-3 text-lg font-bold shadow-brutal-sm active:translate-y-0.5"
          >
            {text}
          </button>
        ))}
      </section>,
    );
  }

  const type = TYPES[result.code];
  return shell(
    <section data-testid="guanxin-result" className="flex flex-col gap-4">
      <div className="flex flex-col items-center gap-1 rounded-2xl border-2 border-black bg-white p-5 text-center shadow-brutal-sm">
        <p className="text-sm text-gray-500">观心镜里,现在的你像一位</p>
        <span className="text-6xl">{type.emoji}</span>
        <h1 data-testid="guanxin-title" className="text-3xl font-extrabold tracking-widest">
          {type.title}
        </h1>
        <p className="text-xs text-gray-400" data-testid="guanxin-code">
          {result.code}
        </p>
        <p className="mt-1">{type.line}</p>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl border-2 border-black bg-white p-4">
        {result.axes.map((a) => (
          <AxisBar key={a.id} axis={a} />
        ))}
      </div>

      <div data-testid="guanxin-strengths" className="rounded-2xl border-2 border-black bg-white p-4">
        <h2 className="mb-2 font-extrabold">🌟 你的长处</h2>
        <ul className="flex list-disc flex-col gap-1 pl-5">
          {type.strengths.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </div>

      <div data-testid="guanxin-grow" className="rounded-2xl border-2 border-black bg-white p-4">
        <h2 className="mb-2 font-extrabold">🌱 可以多练的地方</h2>
        <ul className="flex list-disc flex-col gap-1 pl-5">
          {type.grow.map((s) => (
            <li key={s}>{s}</li>
          ))}
        </ul>
      </div>

      <p className="flex items-start gap-2 text-sm">
        <span className="text-2xl">{guardianIcon}</span>
        <span>
          {guardianName}:每种性格都有自己的本事。你还在长大,过一阵子再来照,镜子里的你可能会不一样哦!
        </span>
      </p>

      <button
        type="button"
        data-testid="guanxin-again"
        onClick={start}
        className="rounded-2xl border-2 border-black bg-qiji-gold px-4 py-3 text-lg font-extrabold shadow-brutal-sm active:translate-y-0.5"
      >
        再照一次
      </button>

      <p data-testid="guanxin-parent-note" className="rounded-xl bg-amber-50 p-3 text-xs leading-5 text-gray-500">
        给家长:观心亭参照 MBTI 的四组性格倾向({AXES.map((a) => `${a.left.name}/${a.right.name}`).join('、')}
        )编写,用来聊天和认识自己,不是诊断或评价。孩子的性格还在形成,结果随年龄和心情变化很正常。结果只保存在这台设备上,不会上传。
      </p>
    </section>,
  );
}

export default GuanxinPage;
